
import { MenuInteractionsService } from './../../../shell/services/menu-interactions.service';
import { MenuType } from './../../enums/menuType';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { AppSettingsService } from '../../services/app-settings.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-vertical-menu-item',
    templateUrl: './vertical-menu-item.component.html',
    styleUrls: ['./vertical-menu-item.component.scss'],
    standalone: false
})
export class VerticalMenuItemComponent implements OnInit, OnDestroy {
  @Input() dataSource: any;
  isExpanded: boolean;
  queryParams: any;
  currentOrganizationId: number;
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly appSettingsService: AppSettingsService,
    private readonly menuInteractionsService: MenuInteractionsService

  ) { }

  ngOnInit() {

    this.appSettingsService.organizationChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((organization) => {
      this.currentOrganizationId = organization.id;
    });

    this.menuInteractionsService.interactionDone$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((interaction) => {
      if(this.dataSource.menuType !== interaction.itemMenuType){
        this.isExpanded = false;
      }
    });

    this.setIsBottom();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setIsBottom();
        if (this.isExpandable()) {
          this.isExpanded = this.isThisMenuItemActive();
          if(this.dataSource.level === 0){
            const moduleName = this.dataSource.menuItemRouterLink.split('/')[1];
            if(event.url.includes(moduleName)){
              this.isExpanded = true;
            }
          }
        }
      }
    });

    if (this.isExpandable()) {
      this.isExpanded = this.isThisMenuItemActive() || this.dataSource.isActive;
    }

    this.queryParams = this.getQueryParamsIfAny();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setIsBottom() {
    if (this.dataSource.isActive === true) {
      this.dataSource.isBottom = true;
    }
  }

  isExpandable() {
    if (this.dataSource.menuItems) {
      return (
        this.dataSource.menuItems !== undefined &&
        this.dataSource.menuItems.length > 0
      );
    } else {
      return false;
    }
  }

  onArrowClick(event) {
    event.stopPropagation();

    if (this.isExpandable()) {
      this.isExpanded = !this.isExpanded;
    }

    if(this.isExpanded){
      this.menuInteractionsService.setMenuInteraction(this.dataSource.menuType,this.dataSource.menuItemRouterLink);
    }

    return false;
  }

  onMenuItemClick() {
    this.menuInteractionsService.setMenuInteraction(this.dataSource.menuType,this.dataSource.menuItemRouterLink);
    if (this.isExpandable()) {
      this.isExpanded = !this.isExpanded;
    }
  }


  private getQueryParamsIfAny() {
    if (this.dataSource.menuItemRouterLink) {
      if(this.dataSource.needsQueryParams && this.shouldPersistQueryParams()) {
        if (this.dataSource.queryParamsFunc) {
          return this.dataSource.queryParamsFunc(this.dataSource);
        } else {
          return this.router.parseUrl(this.location.path()).queryParams;
        }
      }
    }
  }

  shouldPersistQueryParams() {
    const currentRoute = this.router.url.substring(0, this.router.url.lastIndexOf('/'));
    const currentRouteEnding = currentRoute.substring(currentRoute.lastIndexOf('/'));
    const nextRoute = this.dataSource.menuItemRouterLink.substring(0, this.dataSource.menuItemRouterLink.lastIndexOf('/'));
    const nextRouteEnding = nextRoute.substring(nextRoute.lastIndexOf('/'));
    return currentRouteEnding === nextRouteEnding;
  }

  isThisMenuItemActive() {
    return (
      (this.dataSource.menuItems &&
        this.dataSource.level >= 0 &&
        this.dataSource.menuItems.find((n) => this.isMenuItemActive(n))) ||
      (this.dataSource.menuItemRouterLink &&
        this.router.url.indexOf(this.dataSource.menuItemRouterLink) >= 0 &&
        (this.dataSource.menuItemActiveFunc === undefined || this.dataSource.menuItemActiveFunc()))
    );
  }

  isMenuItemActive(menuItem) {
    return (
      (menuItem.menuItemRouterLink &&
        this.dataSource.level >= 0 &&
        this.router.url.indexOf(menuItem.menuItemRouterLink) >= 0) ||
      (menuItem.menuItems && menuItem.menuItems.find((n) => this.isMenuItemActive(n)))
    );
  }

  get menuItemClassesWithLinks() {
    const menuTypeClass = this.getMenuTypeClassName(this.dataSource.menuType);
    const menuActiveClass = this.getMenuTypeActiveClassName(this.dataSource.menuType, this.dataSource.level);
    const subMenuItemClass = this.getSubMenuTypeItemClassName(this.dataSource.menuType,this.dataSource.level);

    return { [`${this.dataSource.className}`]: !!this.dataSource.className,
            [`${menuTypeClass}`]: this.dataSource.isActive,
            [`${menuActiveClass}`]: this.isThisMenuItemActive() && this.dataSource.level > 0,
            [`${subMenuItemClass}`]: this.dataSource.level > 0 && !this.isThisMenuItemActive() };
  }

  get menuItemClassesNoLinks() {
    const menuTypeClass = this.getMenuTypeClassName(this.dataSource.menuType);
    const menuActiveClass = this.getMenuTypeActiveClassName(this.dataSource.menuType, this.dataSource.level);
    const subMenuItemClass = this.getSubMenuTypeItemClassName(this.dataSource.menuType,this.dataSource.level);

    return {[`${menuTypeClass}`]: this.dataSource.isActive,
            [`${menuActiveClass}`]: this.isThisMenuItemActive(),
            [`${subMenuItemClass}`]: this.dataSource.level > 0 };
  }

  getMenuTypeClassName(menuType: MenuType) {
    if(menuType === MenuType.Manager) {
      return 'manager-menu-item';
    } else if (menuType === MenuType.Grower) {
      return 'grower-menu-item';
    } else {
      return 'generic-menu-item';
    }
  }

  getMenuTypeClass(menuType: MenuType) {
    if( menuType === MenuType.Manager) {
      return {'manager-menu-item': this.dataSource.isActive || this.dataSource.level === 0};
    } else if (menuType === MenuType.Grower) {
      return {'grower-menu-item': this.dataSource.isActive || this.dataSource.level === 0};
    } else {
      return {'generic-menu-item': this.dataSource.isActive || this.dataSource.level === 0};
    }
  }

  private getMenuTypeActiveClassName(menuType: MenuType, level: number) {
    if( menuType === MenuType.Manager) {
      return level <= 1? 'manager-active-sub-menu-item' : 'manager-active-inner-menu-item';
    } else if ( menuType === MenuType.Grower) {
      return level <= 1? 'grower-active-sub-menu-item': 'grower-active-inner-menu-item';
    } else {
      return 'generic-active-sub-menu-item';
    }
  }

  private getSubMenuTypeItemClassName(menuType: MenuType,level: number) {
    if( menuType === MenuType.Manager) {
      return level <= 1?'manager-sub-menu-item':'manager-inner-menu-item';
    } else if ( menuType === MenuType.Grower) {
      return level <= 1?'grower-sub-menu-item':'grower-inner-menu-item';
    } else {
      return 'generic-sub-menu-item';
    }
  }

  getPadding(){
    return 0.3 + (this.dataSource.level > 0 ? this.dataSource.level * 0.7 : 0);
  }
}
